// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: a Markdown document drawn by Konvo itself, in its own type and the current theme (unlike
// HTML pages, which keep their own look). Konvo's chat Markdown draws the headings, lists, tables,
// quotes and code; front matter, rules, task lists, pictures, tables with pictures in them, folded
// `<details>` sections and raw HTML blocks are drawn here (markdownDocumentBlocks). Pictures load
// through `loadPicture`, from wherever the document came. With `openPictures` a tap on one opens
// it in the media viewer, stepping through every picture in the document (or in the gallery it
// sits in). The source's `videos`, each an address on a line of its own, draw with `renderVideo`.

import {DocumentPicture, type MarkdownPictureLoader} from '@app/features/document_preview/DocumentPicture';
import styles from '@app/features/document_preview/DocumentPreview.module.css';
import {
	type DocumentBlock,
	inlineHtmlToMarkdown,
	markdownDocumentBlocks,
	type DocumentPicture as Picture,
	pictureParts,
} from '@app/features/document_preview/DocumentPreviewUtils';
import {MarkdownHtml} from '@app/features/document_preview/MarkdownHtml';
import {SafeMarkdown} from '@app/features/messaging/components/markdown';
import markupStyles from '@app/features/theme/styles/Markup.module.css';
import {MediaGallery, useInMediaGallery} from '@app/features/ui/components/MediaGallery';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {forwardRef, type ReactNode, useMemo} from 'react';

interface PictureOptions {
	load?: MarkdownPictureLoader;
	open: boolean;
	renderVideo?: (src: string) => ReactNode;
}

export const MarkdownDocument = forwardRef<
	HTMLDivElement,
	{
		markdown: string;
		compact?: boolean;
		/** Drawn inside another view (a task's description): in its type, with every line break kept. */
		embedded?: boolean;
		className?: string;
		loadPicture?: MarkdownPictureLoader;
		/** A tap on a picture opens the document's pictures in the media viewer. */
		openPictures?: boolean;
		/** Addresses that are videos when they stand on a line of their own, drawn by `renderVideo`. */
		videos?: ReadonlyArray<string>;
		renderVideo?: (src: string) => ReactNode;
	}
>(function MarkdownDocument(
	{markdown, compact = false, embedded = false, className, loadPicture, openPictures = false, videos, renderVideo},
	ref,
) {
	const videoKey = renderVideo ? (videos ?? []).join('\n') : '';
	const blocks = useMemo(
		() =>
			markdownDocumentBlocks(markdown, {
				keepLineBreaks: embedded,
				videos: videoKey ? new Set(videoKey.split('\n')) : undefined,
			}),
		[markdown, embedded, videoKey],
	);
	const inGallery = useInMediaGallery();
	const content = (
		<div
			ref={ref}
			className={clsx(
				markupStyles.markup,
				styles.document,
				compact && styles.documentCompact,
				embedded && styles.documentEmbedded,
				className,
			)}
			data-flx="document-preview.markdown"
		>
			<DocumentBlocks
				blocks={blocks}
				compact={compact}
				pictures={{load: loadPicture, open: openPictures, renderVideo}}
			/>
		</div>
	);
	// The pictures open as one set, in the order they are on the page; inside a gallery (a pull
	// request's comments), with the rest of it.
	return openPictures && !inGallery ? <MediaGallery>{content}</MediaGallery> : content;
});

function DocumentBlocks({
	blocks,
	compact,
	pictures,
}: {
	blocks: Array<DocumentBlock>;
	compact: boolean;
	pictures: PictureOptions;
}) {
	const {t} = useLingui();
	return blocks.map((block, index) => {
		switch (block.kind) {
			case 'text':
				return <SafeMarkdown key={index} content={block.text} />;
			case 'rule':
				return <hr key={index} className={styles.rule} />;
			case 'html':
				return <MarkdownHtml key={index} html={block.html} load={pictures.load} openPictures={pictures.open} />;
			case 'pictures':
				return (
					<p key={index} className={styles.pictures}>
						{block.items.map((item, itemIndex) => (
							<LinkedPicture key={itemIndex} picture={item} pictures={pictures} />
						))}
					</p>
				);
			case 'table':
				return <PictureTable key={index} table={block} pictures={pictures} />;
			case 'video':
				return <div key={`${index}:${block.src}`}>{pictures.renderVideo?.(block.src)}</div>;
			case 'details':
				return (
					<details key={index} className={styles.details} open={block.open || undefined}>
						<summary>
							{block.summary ||
								t({message: 'Details', comment: 'Document preview: a folded section that has no title of its own.'})}
						</summary>
						<DocumentBlocks blocks={block.blocks} compact={compact} pictures={pictures} />
					</details>
				);
			case 'facts':
				// A card's preview starts at the title; the viewer shows the front matter.
				if (compact) return null;
				return (
					<table key={index} className={styles.facts}>
						<tbody>
							{block.rows.map(([name, value]) => (
								<tr key={name}>
									<th scope="row">{name}</th>
									<td>{value}</td>
								</tr>
							))}
						</tbody>
					</table>
				);
			case 'tasks':
				return (
					<ul key={index} className={styles.tasks}>
						{block.items.map((item, itemIndex) => (
							<li
								key={itemIndex}
								className={clsx(styles.task, item.done && styles.taskDone)}
								style={item.depth ? {marginLeft: item.depth * 22} : undefined}
							>
								<span
									className={styles.box}
									role="img"
									aria-label={
										item.done
											? t({message: 'Done', comment: 'Document preview: a ticked checklist item.'})
											: t({message: 'To do', comment: 'Document preview: an unticked checklist item.'})
									}
									data-done={item.done || undefined}
								/>
								<span className={styles.taskText}>
									<SafeMarkdown content={item.text} />
								</span>
							</li>
						))}
					</ul>
				);
			default:
				return null;
		}
	});
}

/** A picture, inside its link when it has a web one (then a tap follows the link). */
function LinkedPicture({picture, pictures}: {picture: Picture; pictures: PictureOptions}) {
	const linked = picture.href && /^https?:/i.test(picture.href) ? picture.href : undefined;
	const drawn = (
		<DocumentPicture
			src={picture.src}
			alt={picture.alt}
			width={picture.width}
			height={picture.height}
			load={pictures.load}
			open={pictures.open && !linked}
		/>
	);
	return linked ? (
		<a href={linked} target="_blank" rel="noreferrer noopener">
			{drawn}
		</a>
	) : (
		drawn
	);
}

const ALIGN_CLASS = {left: markupStyles.alignLeft, center: markupStyles.alignCenter, right: markupStyles.alignRight};

/** A table with pictures in it, drawn like chat Markdown's tables with the pictures in their cells. */
function PictureTable({table, pictures}: {table: Extract<DocumentBlock, {kind: 'table'}>; pictures: PictureOptions}) {
	const cell = (text: string): ReactNode =>
		// A cell's `<br>` is its line break.
		pictureParts(text.replace(/<br\s*\/?>/gi, '\n')).map((part, index) =>
			part.kind === 'text' ? (
				<SafeMarkdown key={index} content={inlineHtmlToMarkdown(part.text.trim())} />
			) : (
				<LinkedPicture key={index} picture={part.picture} pictures={pictures} />
			),
		);
	const aligned = (column: number) => {
		const align = table.align[column];
		return align ? ALIGN_CLASS[align] : undefined;
	};
	return (
		<div
			className={clsx(markupStyles.tableContainer, styles.pictureTableBox)}
			data-flx="document-preview.markdown.picture-table"
		>
			<table className={clsx(markupStyles.table, styles.pictureTable)}>
				<thead>
					<tr>
						{table.header.map((text, column) => (
							<th key={column} className={clsx(markupStyles.tableHeader, aligned(column))}>
								{cell(text)}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{table.rows.map((row, rowIndex) => (
						<tr key={rowIndex}>
							{row.map((text, column) => (
								<td key={column} className={clsx(markupStyles.tableCell, aligned(column))}>
									{cell(text)}
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}
